import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import glotaTranslationService from '../services/glota-translation.service';
import { UserPreferencesService } from '../services/user-preferences.service';
import { localStorageService } from 'src/services/local-storage.service';

// Cria o contexto de tradução GLOTA
const GlotaTranslationContext = createContext(null);

// Provider do contexto de tradução GLOTA
export const GlotaTranslationProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false); // Inicia como false pois traduções locais já estão disponíveis
  const [currentLocale, setCurrentLocale] = useState('pt-BR');
  const [translationsReady, setTranslationsReady] = useState(true); // Marca como pronto imediatamente
  const [pendingLocaleFromUrl, setPendingLocaleFromUrl] = useState(null); // Idioma pendente da URL para salvar após login
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Inicializa o serviço de tradução GLOTA
    const initializeTranslations = async () => {
      try {
        // Verifica se há parâmetro de idioma na URL (para áreas não autenticadas)
        // Aceita múltiplos parâmetros: lang, language, locale
        const urlParams = new URLSearchParams(window.location.search);
        const urlLang =
          urlParams.get('lang') ||
          urlParams.get('language') ||
          urlParams.get('locale');

        let targetLocale = 'pt-BR';

        if (urlLang) {
          // Idioma da URL tem prioridade máxima
          let locale = urlLang.toLowerCase();

          // Normaliza formatos comuns
          if (locale === 'pt' || locale === 'pt-br' || locale === 'pt_br') {
            locale = 'pt-BR';
          } else if (
            locale === 'es' ||
            locale === 'es-es' ||
            locale === 'es_es'
          ) {
            locale = 'es-ES';
          }

          // Valida se é um locale suportado
          if (locale === 'pt-BR' || locale === 'es-ES') {
            targetLocale = locale;

            // Salva no localStorage para uso futuro (antes do login)
            try {
              localStorage.setItem('lg', locale.split('-')[0]); // Salva como 'pt' ou 'es'
            } catch (e) {
              console.warn(
                'Não foi possível salvar idioma no localStorage:',
                e
              );
            }

            // Marca para salvar nas preferências do usuário após o login
            setPendingLocaleFromUrl(locale);
          }
        } else {
          // Se não há parâmetro na URL, verifica primeiro se o usuário está autenticado
          // Se estiver autenticado, busca do banco primeiro (fonte de verdade)
          // Se não estiver autenticado ou não conseguir buscar, usa localStorage
          let localeFromLocalStorage = null;
          let localeFromDatabase = null;

          // Tenta ler do localStorage primeiro (para comparar depois)
          try {
            const savedLang = localStorage.getItem('lg');
            if (savedLang) {
              let locale = savedLang.toLowerCase();
              if (locale === 'pt' || locale === 'ptbr') locale = 'pt-BR';
              if (locale === 'es') locale = 'es-ES';

              if (locale === 'pt-BR' || locale === 'es-ES') {
                localeFromLocalStorage = locale;
              }
            }
          } catch (e) {
            console.warn('Não foi possível ler idioma do localStorage:', e);
          }

          // Tenta buscar do banco (prioridade se usuário estiver autenticado)
          // Na rota /redirect o login por sessão ainda não gravou o token; não disparar getLanguage() para evitar request sem Authorization
          const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
          const tokenInfoBefore = localStorageService.getLocalStorageItem({ key: 'user_token_login_info' });
          const isRedirectPage = pathname === '/redirect';
          const hasToken = !!(tokenInfoBefore?.token);

          if (!isRedirectPage && hasToken) {
            try {
              const data = await UserPreferencesService.getLanguage();
              let locale = data.language;
              if (locale === 'pt') locale = 'pt-BR';
              if (locale === 'es') locale = 'es-ES';

              if (locale && (locale === 'pt-BR' || locale === 'es-ES')) {
                localeFromDatabase = locale;

                // Se o banco tem um idioma diferente do localStorage, usa o do banco e atualiza o localStorage
                if (
                  localeFromLocalStorage &&
                  localeFromLocalStorage !== localeFromDatabase
                ) {
                  try {
                    localStorage.setItem('lg', localeFromDatabase.split('-')[0]);
                  } catch (e) {
                    console.warn(
                      'Não foi possível atualizar idioma no localStorage:',
                      e
                    );
                  }
                }

                // Se não havia idioma no localStorage mas temos no banco, salva no localStorage também
                if (!localeFromLocalStorage) {
                  try {
                    localStorage.setItem('lg', localeFromDatabase.split('-')[0]);
                  } catch (e) {
                    console.warn(
                      'Não foi possível salvar idioma no localStorage:',
                      e
                    );
                  }
                }

                // Prioriza o idioma do banco se estiver disponível
                targetLocale = localeFromDatabase;
              }
            } catch (error) {
              // É esperado que falhe antes do login - não é um problema
              if (error?.response?.status !== 401) {
                console.warn(
                  'Não foi possível carregar idioma salvo, usando padrão:',
                  error
                );
              }

              // Se não conseguiu buscar do banco, usa o localStorage (se disponível)
              if (!localeFromDatabase && localeFromLocalStorage) {
                targetLocale = localeFromLocalStorage;
              }
            }
          } else {
            // Em /redirect ou sem token: usa localStorage ou padrão (evita request de language sem token)
            if (localeFromLocalStorage) {
              targetLocale = localeFromLocalStorage;
            }
          }

          // Se não encontrou nem no banco nem no localStorage, usa o padrão (já definido acima)
        }

        // Define locale e marca como pronto (traduções locais já estão disponíveis)
        setCurrentLocale(targetLocale);
        glotaTranslationService.setLocale(targetLocale);

        // Marca traduções como prontas imediatamente (usando traduções locais)
        setTranslationsReady(true);
        setIsLoading(false);

        // Busca atualizações do GLOTA em background apenas se estiver habilitado (sem bloquear renderização)
        if (process.env.REACT_APP_ENABLE_GLOTA === 'true') {
          glotaTranslationService.getTranslations(targetLocale).catch(() => {
            // Ignora erros silenciosamente, já temos traduções locais
          });
        }
      } catch (error) {
        console.error('Erro ao inicializar traduções GLOTA:', error);
        // Mesmo em caso de erro, marca como pronto (tem traduções locais)
        setTranslationsReady(true);
        setIsLoading(false);
      }
    };

    initializeTranslations();
  }, []);

  // Effect para salvar idioma da URL nas preferências do usuário após login
  useEffect(() => {
    if (!pendingLocaleFromUrl) return;

    // Verifica se o usuário está autenticado (tem token no localStorage)
    const checkAndSaveLanguage = async () => {
      try {
        // Verifica se há token de acesso (indicando que o usuário está autenticado)
        const poliAccess = localStorage.getItem('poli_access');

        if (poliAccess) {
          // Usuário está autenticado, tenta salvar nas preferências
          try {
            await UserPreferencesService.updateLanguage(pendingLocaleFromUrl);
            setPendingLocaleFromUrl(null); // Limpa o pendente após salvar
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            return true; // Indica que foi salvo com sucesso
          } catch (error) {
            // Se falhar, não é crítico - o idioma já está sendo usado
            if (error?.response?.status !== 401) {
              console.warn(
                'Não foi possível salvar idioma da URL nas preferências do usuário:',
                error
              );
            }
            return false;
          }
        }
        return false;
      } catch (error) {
        console.warn(
          'Erro ao verificar autenticação para salvar idioma:',
          error
        );
        return false;
      }
    };

    // Verifica imediatamente
    checkAndSaveLanguage().then((saved) => {
      if (!saved) {
        // Se não foi salvo, configura um intervalo para verificar periodicamente
        intervalRef.current = setInterval(async () => {
          const saved = await checkAndSaveLanguage();
          if (saved && intervalRef.current) {
            clearInterval(intervalRef.current);
          }
        }, 2000); // Verifica a cada 2 segundos

        // Limpa o intervalo após 30 segundos para evitar verificação infinita
        timeoutRef.current = setTimeout(() => {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setPendingLocaleFromUrl(null);
        }, 30000);
      }
    });

    // Cleanup function
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [pendingLocaleFromUrl]);

  // Função para alterar o idioma (memoizada para evitar recriação)
  const changeLanguage = useCallback(async (newLocale) => {
    try {
      setIsLoading(true);
      glotaTranslationService.setLocale(newLocale);

      // Força uma atualização completa limpando o cache do serviço
      glotaTranslationService.clearCache();

      setCurrentLocale(newLocale);

      // Salva no localStorage para uso futuro
      try {
        localStorage.setItem('lg', newLocale.split('-')[0]); // Salva como 'pt' ou 'es'
      } catch (e) {
        console.warn('Não foi possível salvar idioma no localStorage:', e);
      }

      // Se o usuário está logado, salva nas preferências do usuário
      try {
        await UserPreferencesService.updateLanguage(newLocale);
      } catch (error) {
        // É esperado que falhe se o usuário não estiver logado - não é um problema
        if (error?.response?.status !== 401) {
          console.warn(
            'Não foi possível salvar idioma nas preferências do usuário:',
            error
          );
        }
      }

      // Busca atualizações do GLOTA em background apenas se estiver habilitado
      if (process.env.REACT_APP_ENABLE_GLOTA === 'true') {
        await glotaTranslationService.getTranslations(newLocale);
      }

      // Marca como pronto após carregar traduções
      setTranslationsReady(true);
      setIsLoading(false);
    } catch (error) {
      console.error('Erro ao alterar idioma GLOTA:', error);
      setTranslationsReady(true);
      setIsLoading(false);
    }
  }, []);

  // Função para traduzir (memoizada para evitar recriação)
  // Agora usa tradução síncrona local primeiro
  const translate = useCallback(
    async (key, params = {}) => {
      // Primeiro tenta tradução síncrona local
      const syncTranslation = glotaTranslationService.translateSync(
        key,
        params,
        currentLocale
      );

      // Se encontrou tradução local e não é apenas a chave, retorna imediatamente
      if (syncTranslation !== key) {
        // Busca do GLOTA em background para atualizar se necessário apenas se estiver habilitado
        if (process.env.REACT_APP_ENABLE_GLOTA === 'true') {
          glotaTranslationService
            .translate(key, params, currentLocale)
            .catch(() => {
              // Ignora erros silenciosamente
            });
        }

        return syncTranslation;
      }

      // Se não encontrou localmente, busca tradução completa
      return await glotaTranslationService.translate(
        key,
        params,
        currentLocale
      );
    },
    [currentLocale]
  );

  // Função para traduzir texto direto (memoizada para evitar recriação)
  const translateText = useCallback(
    async (text, sourceLang = 'pt', targetLang = null) => {
      return await glotaTranslationService.translateText(
        text,
        sourceLang,
        targetLang
      );
    },
    []
  );

  // Função para setLocale (memoizada)
  const setLocale = useCallback((locale) => {
    glotaTranslationService.setLocale(locale);
  }, []);

  // Função para getCurrentLocale (memoizada)
  const getCurrentLocale = useCallback(() => {
    return glotaTranslationService.getCurrentLocale();
  }, []);

  // Valor do contexto (memoizado para evitar recriação)
  const contextValue = useMemo(
    () => ({
      translate,
      translateText,
      currentLocale,
      changeLanguage,
      isLoading,
      translationsReady,
      setLocale,
      getCurrentLocale,
    }),
    [
      translate,
      translateText,
      currentLocale,
      changeLanguage,
      isLoading,
      translationsReady,
      setLocale,
      getCurrentLocale,
    ]
  );

  return (
    <GlotaTranslationContext.Provider value={contextValue}>
      {children}
    </GlotaTranslationContext.Provider>
  );
};

// Hook personalizado para usar o contexto de tradução GLOTA
export const useGlotaTranslationContext = () => {
  const context = useContext(GlotaTranslationContext);

  if (!context) {
    throw new Error(
      'useGlotaTranslationContext deve ser usado dentro de um GlotaTranslationProvider'
    );
  }

  return context;
};

// Hook para tradução imediata (síncrona)
export const useGlotaTranslate = () => {
  const { translate } = useGlotaTranslationContext();

  return (key, params = {}) => {
    // Para tradução imediata, retorna uma Promise
    // Em componentes React, use o hook useGlotaT ou o contexto diretamente
    return translate(key, params);
  };
};

// Hook para tradução com estado (para componentes que precisam do valor traduzido)
export const useGlotaTranslatedText = (key, params = {}) => {
  const [translatedText, setTranslatedText] = useState(key);
  const { translate } = useGlotaTranslationContext();

  useEffect(() => {
    const loadTranslation = async () => {
      const translated = await translate(key, params);
      setTranslatedText(translated);
    };

    loadTranslation();
  }, [key, params, translate]);

  return translatedText;
};

export default GlotaTranslationContext;
